import { useState } from 'preact/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { T, btnIcon, card } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { TopBar } from '@/ui/primitives/TopBar';
import { Spinner } from '@/ui/primitives/Spinner';
import { ErrorBox } from '@/ui/primitives/ErrorBox';
import { referralsRepo } from '@/data/repositories/referrals-repo';
import { useAuthInfo } from '@/features/auth/useAuth';
import { fmt } from '@/ui/utils/format';
import { config } from '@/config';
import type { ComponentChildren } from 'preact';

export function ReferralsScreen() {
  const qc = useQueryClient();
  const auth = useAuthInfo();
  const overview = useQuery({
    queryKey: ['referrals'],
    queryFn: referralsRepo.overview,
    refetchInterval: config.refresh.referralsMs,
  });
  const code = auth.data?.me?.refcode != null ? String(auth.data.me.refcode) : '';

  const [copied, setCopied] = useState(false);

  const collect = useMutation({
    mutationFn: referralsRepo.collect,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['referrals'] });
      qc.invalidateQueries({ queryKey: ['home'] });
    },
  });

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* no-op */ }
  };

  return (
    <div style={{ background: T.bg, minHeight: '100%' }}>
      <TopBar title="Referrals" right={<button style={btnIcon}>{sized(Icons.share, 18)}</button>}/>

      {overview.isError && <ErrorBox message="Couldn't load referrals." onRetry={() => overview.refetch()}/>}

      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{
          padding: 22, borderRadius: 22, position: 'relative', overflow: 'hidden',
          background: `linear-gradient(135deg, ${T.accent} 0%, ${T.accent2} 100%)`,
          color: T.accentInk,
        }}>
          <svg style={{ position: 'absolute', right: -20, top: -20, opacity: 0.18 }}
            width="180" height="180" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="48" fill="none" stroke={T.accentInk} strokeWidth="0.5"/>
            <circle cx="50" cy="50" r="36" fill="none" stroke={T.accentInk} strokeWidth="0.5"/>
            <circle cx="50" cy="50" r="24" fill="none" stroke={T.accentInk} strokeWidth="0.5"/>
            <circle cx="50" cy="50" r="12" fill="none" stroke={T.accentInk} strokeWidth="0.5"/>
          </svg>
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {sized(Icons.gift, 14)}
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 1.5 }}>Your code</span>
            </div>
            <div style={{
              marginTop: 14, fontFamily: T.fontMono, fontSize: 40, fontWeight: 700,
              letterSpacing: 4, fontVariantNumeric: 'tabular-nums',
            }}>{code || '—'}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={onCopy} disabled={!code} style={{
                flex: 1, padding: '12px', borderRadius: 12,
                background: T.accentInk, color: T.accent, border: 'none',
                fontFamily: T.font, fontSize: 13, fontWeight: 600,
                cursor: code ? 'pointer' : 'not-allowed',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                {sized(copied ? Icons.check : Icons.copy, 14, 2)}
                {copied ? 'Copied' : 'Copy code'}
              </button>
              <button style={{
                padding: '12px 14px', borderRadius: 12,
                background: 'rgba(10,12,15,0.18)', color: T.accentInk,
                border: `1px solid rgba(10,12,15,0.25)`,
                fontFamily: T.font, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                {sized(Icons.share, 14)}
                Share
              </button>
            </div>
          </div>
        </div>

        <div style={{
          ...card, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12,
          borderColor: T.line2, background: 'rgba(198,255,61,0.05)',
        }}>
          <span style={{ color: T.accent, flexShrink: 0 }}>{sized(Icons.gift, 20)}</span>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text, lineHeight: 1.4 }}>
            You earn{' '}
            <span style={{ color: T.accent }}>
              {overview.data?.sharepercent != null ? `${overview.data.sharepercent}%` : '—'}
            </span>{' '}
            of every order your referrals finish
          </div>
        </div>

        <div style={{ ...card, padding: 18 }}>
          <div style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
            letterSpacing: 1, fontWeight: 500, marginBottom: 10 }}>How it works</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Bullet><strong style={{ color: T.accent }}>Earn coins</strong> from every successful order placed by users you refer.</Bullet>
            <Bullet>Your referrals enjoy a <strong style={{ color: T.accent }}>bonus on deposits</strong>.</Bullet>
            <Bullet>Collect to your balance anytime — no minimum.</Bullet>
          </div>
        </div>

        <div style={{ ...card, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Earnings</div>
          </div>
          {overview.isPending ? <Spinner/> : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Mini label="Today" value={overview.data?.todayearn ?? 0} accent/>
              <Mini label="Weekly" value={overview.data?.weeklyearn ?? 0}/>
              <Mini label="Referrals" value={overview.data?.referrals ?? 0} mono={false}/>
              <Mini label="Pending" value={overview.data?.pendingearn ?? 0} muted/>
            </div>
          )}
        </div>

        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
            borderBottom: `1px solid ${T.line}` }}>
            <Big label="Total earn" value={overview.data?.totalearn ?? 0}/>
            <Big label="Withdrawn" value={(overview.data as { withdrawn?: number })?.withdrawn ?? 0} muted divider/>
          </div>
          <div style={{
            padding: 18, display: 'flex', alignItems: 'center', gap: 14,
            background: 'rgba(198,255,61,0.04)',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
                letterSpacing: 1, fontWeight: 500 }}>Uncollected</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
                <span style={{ fontFamily: T.fontMono, fontSize: 26, fontWeight: 500,
                  color: T.accent, letterSpacing: -0.5, fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(overview.data?.uncollected ?? 0)}
                </span>
                <span style={{ color: T.accent, fontSize: 13, fontWeight: 600 }}>C</span>
              </div>
            </div>
            <button onClick={() => collect.mutate()}
              disabled={!overview.data?.uncollected || collect.isPending}
              style={{
                padding: '12px 20px', borderRadius: 12,
                background: overview.data?.uncollected ? T.accent : T.surface2,
                color: overview.data?.uncollected ? T.accentInk : T.textMute,
                border: 'none', fontSize: 14, fontWeight: 700,
                cursor: overview.data?.uncollected ? 'pointer' : 'not-allowed',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
              {collect.isPending && <Spinner size={14}/>}
              Collect {sized(Icons.arr, 16, 2)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bullet({ children }: { children: ComponentChildren }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <div style={{ width: 18, height: 18, borderRadius: '50%',
        background: 'rgba(198,255,61,0.12)', color: T.accent,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginTop: 1,
      }}>{sized(Icons.check, 12, 2.5)}</div>
      <p style={{ fontSize: 13, color: T.text, lineHeight: 1.55, margin: 0 }}>{children}</p>
    </div>
  );
}

function Mini({ label, value, accent, muted, mono = true }: {
  label: string; value: number; accent?: boolean; muted?: boolean; mono?: boolean;
}) {
  return (
    <div>
      <div style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase',
        letterSpacing: 1, fontWeight: 500 }}>{label}</div>
      <div style={{
        fontFamily: mono ? T.fontMono : T.font, fontSize: 20, marginTop: 2,
        color: accent ? T.accent : muted ? T.textDim : T.text,
        fontVariantNumeric: 'tabular-nums', fontWeight: 500, letterSpacing: -0.3,
      }}>{fmt(value)}</div>
    </div>
  );
}

function Big({ label, value, muted, divider }: {
  label: string; value: number; muted?: boolean; divider?: boolean;
}) {
  return (
    <div style={{ padding: 18, borderLeft: divider ? `1px solid ${T.line}` : 'none' }}>
      <div style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
        letterSpacing: 1, fontWeight: 500 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 4 }}>
        <span style={{ fontFamily: T.fontMono, fontSize: 22, fontWeight: 500,
          color: muted ? T.textDim : T.text, fontVariantNumeric: 'tabular-nums',
          letterSpacing: -0.5 }}>{fmt(value)}</span>
        <span style={{ fontSize: 11, color: muted ? T.textMute : T.accent,
          fontWeight: 600 }}>C</span>
      </div>
    </div>
  );
}
